import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Chip,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Store,
  Category,
  AttachMoney,
} from "@mui/icons-material";
import "./index.scss";

const ProductSelectionModal = ({
  open,
  onClose,
  onConfirm,
  productDetail,
  selectedVendor,
  selectedCategory,
}) => {
  if (!productDetail) return null;

  const getProductPrice = () => {
    if (!productDetail?.variants?.[0]?.variant_detail) return "N/A";
    const variantDetail = productDetail.variants[0].variant_detail;
    return (
      variantDetail.discounted_price || variantDetail.original_price || "N/A"
    );
  };

  const getInstallmentInfo = () => {
    const installmentPlans =
      productDetail?.variants?.[0]?.variant_detail
        ?.commission_installment_plans;
    if (!installmentPlans) return null;

    return {
      asLowAs: installmentPlans.as_low_as,
      plans: installmentPlans.plans || [],
      processingFees: installmentPlans.processing_fees || [],
    };
  };

  const hasMultipleVariants = () => {
    return productDetail.variants && productDetail.variants.length > 1;
  };

  const installmentInfo = getInstallmentInfo();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="b2b-product-selection-modal"
      PaperProps={{
        className: "b2b-modal-paper",
      }}
    >
      <DialogTitle className="b2b-modal-header">
        <Typography variant="h5" component="div" className="b2b-modal-title">
          Confirm Product Selection
        </Typography>
        <Typography variant="body2" className="b2b-modal-subtitle">
          Please review the product details before proceeding
        </Typography>
      </DialogTitle>

      <DialogContent className="b2b-modal-content">
        <Card className="b2b-product-card">
          <Box className="b2b-product-layout">
            {/* Product Image */}
            {/* {productDetail.img_url && (
              <CardMedia
                component="img"
                className="b2b-product-image"
                image={productDetail.img_url}
                alt={productDetail.title}
              />
            )} */}

            {/* Product Details */}
            <CardContent className="b2b-product-details">
              <Typography
                variant="h6"
                component="div"
                className="b2b-product-title"
              >
                {productDetail.title}
              </Typography>

              <Box className="b2b-product-chips">
                {productDetail.variants?.[0]?.variant_detail?.sold_out ===
                  false && (
                  <Chip
                    label="In Stock"
                    color="success"
                    size="small"
                    className="b2b-chip"
                  />
                )}
                {hasMultipleVariants() && (
                  <Chip
                    label={`${productDetail.variants.length} Variants Available`}
                    color="primary"
                    size="small"
                    variant="outlined"
                    className="b2b-chip"
                  />
                )}
              </Box>

              <Box className="b2b-price-section">
                <Box className="b2b-price-container">
                  <Typography
                    variant="h6"
                    color="primary"
                    className="b2b-price-text"
                  >
                    {hasMultipleVariants() ? "Starting from " : ""}PKR{" "}
                    {getProductPrice()}
                  </Typography>
                </Box>
              </Box>

              <Box className="b2b-vendor-info">
                <Box className="b2b-vendor-item">
                  <Store color="text.secondary" fontSize="small" />
                  <Typography variant="body2" className="b2b-vendor-text">
                    {selectedVendor?.name || selectedVendor?.title}
                  </Typography>
                </Box>
                {selectedCategory && (
                  <Box className="b2b-category-item">
                    <Category color="text.secondary" fontSize="small" />
                    <Typography variant="body2" className="b2b-category-text">
                      {selectedCategory.name}
                    </Typography>
                  </Box>
                )}
              </Box>

              {productDetail.delivery_date && (
                <Typography variant="body2" className="b2b-delivery-info">
                  <strong>Delivery:</strong> {productDetail.delivery_date}
                </Typography>
              )}
            </CardContent>
          </Box>
        </Card>

        {/* Variants Info if multiple variants exist */}
        {hasMultipleVariants() && (
          <Card className="b2b-variants-card">
            <CardContent>
              <Typography variant="h6" className="b2b-variants-title">
                Product Variants
              </Typography>
              <Typography variant="body2" className="b2b-variants-description">
                This product has {productDetail.variants.length} different
                variants available. You'll be able to choose your preferred
                variant in the next step.
              </Typography>

              <Box className="b2b-variants-chips">
                {productDetail.variants.slice(0, 3).map((variant, index) => (
                  <Chip
                    key={index}
                    label={variant.title}
                    size="small"
                    variant="outlined"
                    className="b2b-variant-chip"
                  />
                ))}
                {productDetail.variants.length > 3 && (
                  <Chip
                    label={`+${productDetail.variants.length - 3} more`}
                    size="small"
                    color="primary"
                    className="b2b-variant-chip-more"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Product Description Preview */}
        {productDetail.short_description && (
          <Card className="b2b-description-card">
            <CardContent>
              <Typography variant="h6" className="b2b-description-title">
                Product Description
              </Typography>
              <Box
                className="b2b-description-content"
                dangerouslySetInnerHTML={{
                  __html:
                    productDetail.short_description.substring(0, 1000) + "...",
                }}
              />
            </CardContent>
          </Card>
        )}
      </DialogContent>

      <Divider />

      <DialogActions className="b2b-modal-actions">
        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          startIcon={<Cancel />}
          className="b2b-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          startIcon={<CheckCircle />}
          className="b2b-confirm-button"
        >
          {hasMultipleVariants() ? "Continue" : "Select Product"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSelectionModal;
